import React, { useState } from 'react';
import { startOfToday } from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  Typography,
  IconButton,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AdminButton from 'components/common/AdminButton';

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu'
];

const steps = ['Basic Information', 'Location Details', 'Organizational Assignment'];

function AddLocationModal({ open, onClose, onSave, orgUnits = [], hierarchyDefinition = [] }) {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Basic Information
    locationName: '',
    locationNumber: '',
    
    // Address
    address: {
      street_1: '',
      street_2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA'
    },
    
    // Location Details
    timeZone: 'America/Los_Angeles',
    openDate: startOfToday(),
    closeDate: null,
    ownershipType: 'Company',
    conceptId: '',
    
    // Organizational Assignment
    orgLevel_1: '',
    orgLevel_2: '',
    orgLevel_3: '',
    orgLevel_1_name: '',
    orgLevel_2_name: '',
    orgLevel_3_name: ''
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [derivedOrgUnits, setDerivedOrgUnits] = useState({
    orgLevel_2: null,
    orgLevel_3: null
  });

  // Get active ORGLEVEL_1 units for dropdown
  const availableOrgLevel1Units = React.useMemo(() => {
    // Filter for ORGLEVEL_1 units
    // Check both 'orgLevel' and 'level' properties for backward compatibility
    return orgUnits.filter(unit => {
      const levelValue = unit.orgLevel || unit.level;
      return levelValue === 'ORGLEVEL_1' && unit.isActive !== false;
    });
  }, [orgUnits]);

  // Get labels from hierarchy definition
  const getHierarchyLabel = (levelId) => {
    const level = hierarchyDefinition.find(h => h.levelId === levelId && h.isActive);
    return level?.label || levelId.replace('ORGLEVEL_', 'Level ');
  };

  // When orgLevel_1 changes, derive orgLevel_2 and orgLevel_3
  React.useEffect(() => {
    if (formData.orgLevel_1 && orgUnits.length > 0) {
      // Find the selected ORGLEVEL_1 unit
      const selectedUnit = orgUnits.find(u => u.id === formData.orgLevel_1);
      
      if (selectedUnit) {
        // Get parent units
        const level2Unit = selectedUnit.parentOrgUnitId ? 
          orgUnits.find(u => u.id === selectedUnit.parentOrgUnitId) : null;
        
        const level3Unit = level2Unit?.parentOrgUnitId ? 
          orgUnits.find(u => u.id === level2Unit.parentOrgUnitId) : null;
        
        setDerivedOrgUnits({
          orgLevel_2: level2Unit,
          orgLevel_3: level3Unit
        });

        // Update form data with derived values
        setFormData(prev => ({
          ...prev,
          orgLevel_1_name: selectedUnit.name,
          orgLevel_2: level2Unit?.id || '',
          orgLevel_2_name: level2Unit?.name || '',
          orgLevel_3: level3Unit?.id || '',
          orgLevel_3_name: level3Unit?.name || ''
        }));
      }
    } else {
      setDerivedOrgUnits({
        orgLevel_2: null,
        orgLevel_3: null
      });
      
      setFormData(prev => ({
        ...prev,
        orgLevel_1_name: '',
        orgLevel_2: '',
        orgLevel_2_name: '',
        orgLevel_3: '',
        orgLevel_3_name: ''
      }));
    }
  }, [formData.orgLevel_1, orgUnits]);

  const handleChange = (field, value) => {
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 0: // Basic Information
        if (!formData.locationName.trim()) {
          newErrors.locationName = 'Location name is required';
        }
        // Location number is optional
        break;
        
      case 1: // Location Details
        if (!formData.address.street_1.trim()) {
          newErrors['address.street_1'] = 'Street address is required';
        }
        if (!formData.address.city.trim()) {
          newErrors['address.city'] = 'City is required';
        }
        if (!formData.address.state.trim()) {
          newErrors['address.state'] = 'State is required';
        }
        if (!formData.address.zipCode.trim()) {
          newErrors['address.zipCode'] = 'ZIP code is required';
        }
        if (!formData.timeZone) {
          newErrors.timeZone = 'Time zone is required';
        }
        if (!formData.openDate) {
          newErrors.openDate = 'Open date is required';
        }
        if (!formData.ownershipType) {
          newErrors.ownershipType = 'Ownership type is required';
        }
        break;
        
      case 2: // Organizational Assignment
        if (!formData.orgLevel_1) {
          newErrors.orgLevel_1 = 'Organizational assignment is required';
        }
        break;
        
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    // Validate all steps
    let isValid = true;
    for (let i = 0; i <= 2; i++) {
      if (!validateStep(i)) {
        isValid = false;
      }
    }
    
    if (!isValid) {
      // Show first step with errors
      for (let i = 0; i <= 2; i++) {
        if (!validateStep(i)) {
          setActiveStep(i);
          break;
        }
      }
      return;
    }
    
    setSaving(true);
    try {
      await onSave(formData);
      handleClose();
    } catch (error) {
      console.error('Error saving location:', error);
      setErrors({ submit: 'Failed to create location. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      locationName: '',
      locationNumber: '',
      address: {
        street_1: '',
        street_2: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA'
      },
      timeZone: 'America/Los_Angeles',
      openDate: startOfToday(),
      closeDate: null,
      ownershipType: 'Company',
      conceptId: '',
      orgLevel_1: '',
      orgLevel_2: '',
      orgLevel_3: '',
      orgLevel_1_name: '',
      orgLevel_2_name: '',
      orgLevel_3_name: ''
    });
    setErrors({});
    setActiveStep(0);
    setSaving(false);
    onClose();
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Enter the basic information for the new location
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location Name"
                value={formData.locationName}
                onChange={(e) => handleChange('locationName', e.target.value)}
                error={!!errors.locationName}
                helperText={errors.locationName}
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location Number (Optional)"
                value={formData.locationNumber}
                onChange={(e) => handleChange('locationNumber', e.target.value)}
                error={!!errors.locationNumber}
                helperText={errors.locationNumber || "Leave blank if not applicable"}
              />
            </Grid>
          </Grid>
        );
        
      case 1:
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Address Information
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address 1"
                  value={formData.address.street_1}
                  onChange={(e) => handleChange('address.street_1', e.target.value)}
                  error={!!errors['address.street_1']}
                  helperText={errors['address.street_1']}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address 2"
                  value={formData.address.street_2}
                  onChange={(e) => handleChange('address.street_2', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.address.city}
                  onChange={(e) => handleChange('address.city', e.target.value)}
                  error={!!errors['address.city']}
                  helperText={errors['address.city']}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.address.state}
                  onChange={(e) => handleChange('address.state', e.target.value.toUpperCase())}
                  inputProps={{ maxLength: 2 }}
                  error={!!errors['address.state']}
                  helperText={errors['address.state']}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={formData.address.zipCode}
                  onChange={(e) => handleChange('address.zipCode', e.target.value)}
                  error={!!errors['address.zipCode']}
                  helperText={errors['address.zipCode']}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Operational Details
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.timeZone}>
                  <InputLabel>Time Zone</InputLabel>
                  <Select
                    value={formData.timeZone}
                    onChange={(e) => handleChange('timeZone', e.target.value)}
                    label="Time Zone"
                  >
                    {TIMEZONES.map((tz) => (
                      <MenuItem key={tz} value={tz}>
                        {tz.replace('America/', '').replace('Pacific/', '').replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Open Date"
                  value={formData.openDate}
                  onChange={(value) => handleChange('openDate', value)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!errors.openDate,
                      helperText: errors.openDate
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.ownershipType}>
                  <InputLabel>Ownership Type</InputLabel>
                  <Select
                    value={formData.ownershipType}
                    onChange={(e) => handleChange('ownershipType', e.target.value)}
                    label="Ownership Type"
                  >
                    <MenuItem value="Company">Company</MenuItem>
                    <MenuItem value="Franchise">Franchise</MenuItem>
                    <MenuItem value="Licensed">Licensed</MenuItem>
                    <MenuItem value="Joint Venture">Joint Venture</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Concept"
                  value={formData.conceptId}
                  onChange={(e) => handleChange('conceptId', e.target.value)}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        );
        
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Assign this location to an organizational unit
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!errors.orgLevel_1}>
                <InputLabel>{getHierarchyLabel('ORGLEVEL_1')} Assignment</InputLabel>
                <Select
                  value={formData.orgLevel_1}
                  onChange={(e) => handleChange('orgLevel_1', e.target.value)}
                  label={`${getHierarchyLabel('ORGLEVEL_1')} Assignment`}
                >
                  <MenuItem value="">
                    <em>None (Unassigned)</em>
                  </MenuItem>
                  {availableOrgLevel1Units.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.orgLevel_1 && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.orgLevel_1}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            {/* Show derived parent units */}
            {derivedOrgUnits.orgLevel_2 && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={getHierarchyLabel('ORGLEVEL_2')}
                  value={derivedOrgUnits.orgLevel_2.name}
                  disabled
                  helperText="Inherited from selected unit"
                />
              </Grid>
            )}
            
            {derivedOrgUnits.orgLevel_3 && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={getHierarchyLabel('ORGLEVEL_3')}
                  value={derivedOrgUnits.orgLevel_3.name}
                  disabled
                  helperText="Inherited from selected unit"
                />
              </Grid>
            )}
            
            {!formData.orgLevel_1 && (
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined">
                  Select a {getHierarchyLabel('ORGLEVEL_1').toLowerCase()} to see the full organizational hierarchy
                </Alert>
              </Grid>
            )}
          </Grid>
        );
        
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <StoreIcon color="primary" />
            <Typography variant="h6">Add New Location</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.submit}
          </Alert>
        )}
        
        <Box sx={{ minHeight: '300px' }}>
          {renderStepContent(activeStep)}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={saving}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <AdminButton variant="contained" onClick={handleNext}>
            Next
          </AdminButton>
        ) : (
          <AdminButton 
            variant="contained" 
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Creating...' : 'Create Location'}
          </AdminButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AddLocationModal;